import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { useAuth } from '@/lib/auth/auth-context'
import { initialsOf } from '@/lib/scoring/presentation'

export function AccountMenu() {
  const { session, signOut } = useAuth()
  const [open, setOpen] = useState(false)

  const menuId = useId()
  const containerRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const name = session?.displayName ?? ''
  const email = session?.email ?? ''
  const showEmail = email !== '' && email !== name

  return (
    <div
      className="canopy-account"
      ref={containerRef}
      onMouseEnter={() => {
        setOpen(true)
      }}
      onMouseLeave={(event) => {
        const leavingTo: unknown = event.relatedTarget

        if (leavingTo === null) {
          close()
          return
        }

        const isNode = typeof leavingTo === 'object' && 'nodeType' in leavingTo

        if (isNode && containerRef.current?.contains(leavingTo as Node) !== true) close()
      }}
    >
      <button
        type="button"
        className="canopy-avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={session ? `${name} — account menu` : 'Account menu'}
        onClick={() => {
          setOpen(true)
        }}
      >
        {initialsOf(session?.displayName)}
      </button>

      {open && <div className="canopy-account-cap" aria-hidden="true" />}

      {open && (
        <div className="canopy-account-menu" id={menuId} role="menu">
          <div className="canopy-account-identity">
            <div className="canopy-account-name" title={name === '' ? undefined : name}>
              {name === '' ? '' : name}
            </div>
            {showEmail && (
              <div className="canopy-account-email" title={email}>
                {email}
              </div>
            )}
          </div>

          <button
            type="button"
            role="menuitem"
            className="canopy-account-signout"
            onClick={() => {
              close()
              void signOut()
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
